import { useState } from "react";
import { App, Button, Form, Input, Modal, Popconfirm, Select } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "@/core/api/crmApi";
import { tenantWorkspaceApi } from "@/core/api/tenantWorkspaceApi";
import type {
  Contact,
  ContactConsentChannel,
  ContactConsentStatus,
  UpdateCustomerProfileRequest,
} from "@/core/types/crm";
import type {
  CustomerLanding,
  CustomerLandingEvent,
  CustomerRetailPurchase,
  PersonalLandingItem,
} from "@/core/types/tenantWorkspace";
import { formatDateTime } from "@/core/utils/date";
import { getErrorMessage } from "@/core/utils/errors";
import { AppIcon } from "@/shared/components/AppIcon";

interface CustomerProfileDrawerProps {
  contact: Contact;
  canViewRetailHistory: boolean;
  canRevokeLanding: boolean;
  canManageProfile: boolean;
  onClose: () => void;
}

const interestLabels: Record<string, string> = {
  Viewed: "مشاهده‌شده",
  AskedPrice: "استعلام قیمت",
  Tried: "بررسی‌شده",
  Interested: "علاقه‌مند",
  Reserved: "رزرو‌شده",
  Purchased: "خریداری‌شده",
  Rejected: "ردشده",
};
const eventLabels: Record<string, string> = {
  LandingSent: "لینک ساخته شد",
  LandingOpened: "صفحه باز شد",
  ProductViewed: "کالا/خدمت مشاهده شد",
  CTA_Clicked: "تماس انتخاب شد",
  Reserved: "رزرو شد",
  Purchased: "خرید ثبت شد",
};
const itemSpecificEventTypes = new Set([
  "ProductViewed",
  "CTA_Clicked",
  "Reserved",
  "Purchased",
]);
const statusLabels: Record<string, string> = {
  Created: "ساخته‌شده",
  Sent: "ارسال‌شده",
  Reserved: "رزرو‌شده",
  Purchased: "خریداری‌شده",
  Revoked: "غیرفعال‌شده",
  Expired: "منقضی‌شده",
};
const statusTone: Record<string, string> = {
  Created: "bg-slate-100 text-slate-600",
  Sent: "bg-sky-50 text-sky-700",
  Reserved: "bg-violet-50 text-violet-700",
  Purchased: "bg-emerald-50 text-emerald-700",
  Revoked: "bg-rose-50 text-rose-700",
  Expired: "bg-amber-50 text-amber-700",
};
const paymentMethodLabels: Record<string, string> = {
  Cash: "نقدی",
  Card: "کارت‌خوان",
  Transfer: "انتقال بانکی",
  Online: "آنلاین",
  Credit: "اعتباری",
  Multiple: "ترکیبی",
};
const invoiceStatusLabels: Record<string, string> = {
  Completed: "تکمیل‌شده",
  PartiallyReturned: "مرجوعی جزئی",
  Returned: "مرجوع‌شده",
  Cancelled: "لغو شده",
  Voided: "باطل‌شده",
};
const paymentStatusLabels: Record<string, string> = {
  Paid: "پرداخت‌شده",
  Unpaid: "پرداخت‌نشده",
  PartiallyPaid: "پرداخت ناقص",
  PartiallyRefunded: "مرجوعی جزئی",
  Refunded: "مرجوع‌شده",
};
const deliveryLabels: Record<string, string> = {
  Sent: "ارسال شد",
  Skipped: "ارسال نشد",
  Failed: "ناموفق",
  Pending: "در صف ارسال",
};
const money = (value: number | null, currency: string): string =>
  value === null
    ? "قیمت ثبت نشده"
    : `${new Intl.NumberFormat("fa-IR").format(value)} ${currency === "IRR" ? "ریال" : currency}`;
const contactName = (contact: Contact): string =>
  [contact.firstName, contact.lastName].filter(Boolean).join(" ") ||
  "مخاطب بدون نام";
type CustomerProfileForm = {
  addresses?: Array<{
    label?: string;
    province?: string;
    city?: string;
    addressLine?: string;
    postalCode?: string;
    isPrimary?: boolean;
  }>;
  tags?: string;
  smsConsent: ContactConsentStatus;
  smsConsentSource?: string;
  emailConsent: ContactConsentStatus;
  emailConsentSource?: string;
};
const consentLabel: Record<string, string> = {
  granted: "مجاز",
  withdrawn: "لغو شده",
  unknown: "نامشخص",
};
const consentTone: Record<string, string> = {
  granted: "bg-emerald-50 text-emerald-700",
  withdrawn: "bg-rose-50 text-rose-700",
  unknown: "bg-slate-100 text-slate-600",
};
const currentConsent = (
  contact: Contact,
  channel: ContactConsentChannel,
): { status: ContactConsentStatus; source: string } => {
  const item = contact.consents?.find(
    (consent) => consent.channel.toLowerCase() === channel,
  );
  return {
    status:
      (item?.status?.toLowerCase() as ContactConsentStatus | undefined) ??
      "unknown",
    source: item?.source ?? "",
  };
};

const landingItems = (landing: CustomerLanding): PersonalLandingItem[] => {
  if (landing.items?.length) {
    return landing.items.filter(
      (item, index, items) =>
        items.findIndex(
          (candidate) =>
            (candidate.catalogItemId || candidate.productId) ===
            (item.catalogItemId || item.productId),
        ) === index,
    );
  }
  return [
    {
      catalogItemId: landing.productId,
      productId: landing.productId,
      name: landing.productName,
      productName: landing.productName,
      itemType: "Product",
      imageUrl: landing.imageUrl,
      price: landing.price,
      previousPrice: landing.previousPrice,
      currency: landing.currency,
    },
  ];
};

const itemTypeLabel = (itemType: PersonalLandingItem["itemType"]): string =>
  itemType === "Service" ? "خدمت" : "کالا";

const PurchaseItems = ({
  purchase,
}: {
  purchase: CustomerRetailPurchase;
}): React.ReactElement => (
  <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
    {purchase.items.map((item) => (
      <div
        className="flex items-center justify-between gap-3 text-xs"
        key={`${purchase.invoiceId}-${item.catalogItemId}`}
      >
        <div className="min-w-0">
          <p className="truncate font-bold text-slate-700">{item.name}</p>
          <p className="mt-0.5 text-[11px] text-slate-400">
            {item.itemType === "Service" ? "خدمت" : "کالا"}
            {item.sku ? ` · ${item.sku}` : ""} ·{" "}
            {new Intl.NumberFormat("fa-IR").format(item.quantity)} عدد
          </p>
        </div>
        <span className="shrink-0 font-bold text-slate-600">
          {money(item.lineTotal, purchase.currency)}
        </span>
      </div>
    ))}
  </div>
);

const EventSummary = ({
  events,
  items,
}: {
  events: CustomerLandingEvent[];
  items: PersonalLandingItem[];
}): React.ReactElement => {
  const counts = events.reduce<Record<string, number>>((result, event) => {
    result[event.eventType] = (result[event.eventType] ?? 0) + 1;
    return result;
  }, {});
  const itemNames = new Map(
    items.flatMap((item) =>
      [item.catalogItemId, item.productId]
        .filter(Boolean)
        .map((id) => [id, item.name] as const),
    ),
  );
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {Object.entries(counts).map(([type, count]) => (
          <span
            className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600"
            key={type}
          >
            {eventLabels[type] ?? type}
            {count > 1
              ? ` · ${new Intl.NumberFormat("fa-IR").format(count)}`
              : ""}
          </span>
        ))}
      </div>
      {events.length ? (
        <div className="mt-3 grid gap-1 text-[11px] text-slate-400 sm:grid-cols-2">
          {events.slice(0, 6).map((event) => (
            <p className="truncate" key={event.id}>
              {eventLabels[event.eventType] ?? event.eventType}
              {itemSpecificEventTypes.has(event.eventType) &&
              event.productId &&
              itemNames.get(event.productId)
                ? ` · ${itemNames.get(event.productId)}`
                : ""}
              : {formatDateTime(event.occurredAtUtc)}
            </p>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400">
          هنوز رویدادی برای این صفحه ثبت نشده است.
        </p>
      )}
    </div>
  );
};

export const CustomerProfileDrawer = ({
  contact,
  canViewRetailHistory,
  canRevokeLanding,
  canManageProfile,
  onClose,
}: CustomerProfileDrawerProps): React.ReactElement => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"profile" | "retail">("profile");
  const [editingProfile, setEditingProfile] = useState(false);
  const [pointsAdjustment, setPointsAdjustment] = useState(0);
  const [pointsAdjustmentNote, setPointsAdjustmentNote] = useState("");
  const [profileForm] = Form.useForm<CustomerProfileForm>();
  const profile = useQuery({
    queryKey: ["contact", contact.id],
    queryFn: () => crmApi.contact(contact.id),
  });
  const customer = profile.data ?? contact;
  const history = useQuery({
    queryKey: ["retail", "customer-history", contact.id],
    queryFn: () => tenantWorkspaceApi.customerRetailHistory(contact.id),
    enabled: canViewRetailHistory && tab === "retail",
  });
  const loyalty = useQuery({
    queryKey: ["retail", "loyalty", "customer", contact.id],
    queryFn: () => tenantWorkspaceApi.customerLoyalty(contact.id),
    enabled: canViewRetailHistory && tab === "retail",
  });
  const revoke = useMutation({
    mutationFn: tenantWorkspaceApi.revokeCustomerLanding,
    onSuccess: async () => {
      message.success("لینک اختصاصی غیرفعال شد.");
      await queryClient.invalidateQueries({
        queryKey: ["retail", "customer-history", contact.id],
      });
    },
    onError: (error) => message.error(getErrorMessage(error)),
  });
  const adjustLoyaltyPoints = useMutation({
    mutationFn: () =>
      tenantWorkspaceApi.adjustCustomerLoyaltyPoints(contact.id, {
        pointsDelta: pointsAdjustment,
        note: pointsAdjustmentNote.trim(),
      }),
    onSuccess: async () => {
      setPointsAdjustment(0);
      setPointsAdjustmentNote("");
      message.success("امتیاز مشتری اصلاح شد.");
      await queryClient.invalidateQueries({
        queryKey: ["retail", "loyalty", "customer", contact.id],
      });
    },
    onError: (error) => message.error(getErrorMessage(error)),
  });
  const saveProfile = useMutation({
    mutationFn: (request: UpdateCustomerProfileRequest) =>
      crmApi.updateCustomerProfile(contact.id, customer.rowVersion, request),
    onSuccess: async () => {
      message.success("پرونده مشتری به‌روزرسانی شد.");
      setEditingProfile(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contact", contact.id] }),
        queryClient.invalidateQueries({ queryKey: ["contacts"] }),
      ]);
    },
    onError: (error) => message.error(getErrorMessage(error)),
  });
  const copy = async (value: string): Promise<void> => {
    await navigator.clipboard.writeText(value);
    message.success("لینک کپی شد.");
  };
  const startProfileEdit = (): void => {
    const sms = currentConsent(customer, "sms");
    const email = currentConsent(customer, "email");
    profileForm.setFieldsValue({
      addresses:
        customer.addresses?.map((address) => ({
          label: address.label ?? "",
          province: address.province ?? "",
          city: address.city ?? "",
          addressLine: address.addressLine ?? "",
          postalCode: address.postalCode ?? "",
          isPrimary: address.isPrimary,
        })) ?? [],
      tags: customer.tags?.join("، ") ?? "",
      smsConsent: sms.status,
      smsConsentSource: sms.source,
      emailConsent: email.status,
      emailConsentSource: email.source,
    });
    setEditingProfile(true);
  };
  const submitProfile = (values: CustomerProfileForm): void => {
    const consentItems: UpdateCustomerProfileRequest["consents"] = [
      {
        channel: "sms",
        status: values.smsConsent ?? "unknown",
        source: values.smsConsentSource?.trim() || null,
      },
      {
        channel: "email",
        status: values.emailConsent ?? "unknown",
        source: values.emailConsentSource?.trim() || null,
      },
    ];
    saveProfile.mutate({
      addresses: (values.addresses ?? [])
        .filter(
          (address) =>
            address.addressLine?.trim() ||
            address.city?.trim() ||
            address.province?.trim(),
        )
        .map((address) => ({
          label: address.label?.trim() || null,
          province: address.province?.trim() || null,
          city: address.city?.trim() || null,
          addressLine: address.addressLine?.trim() || null,
          postalCode: address.postalCode?.trim() || null,
          isPrimary: Boolean(address.isPrimary),
        })),
      tags: (values.tags ?? "")
        .split(/[،,]/)
        .map((tag) => tag.trim())
        .filter(Boolean),
      consents: consentItems,
    });
  };

  return (
    <div className="fixed inset-0 z-[1000]" dir="rtl">
      <button
        aria-label="بستن پرونده مشتری"
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-[2px]"
        onClick={onClose}
        type="button"
      />
      <section
        aria-modal="true"
        className="absolute inset-y-0 left-0 flex w-full flex-col overflow-hidden bg-slate-50 shadow-2xl sm:max-w-3xl"
        role="dialog"
      >
        <header className="border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-bold text-brand-600">
                پرونده مشتری ۳۶۰ درجه
              </p>
              <h2 className="mt-1 truncate text-xl font-black text-ink-950">
                {contactName(customer)}
              </h2>
              <p className="mt-1 text-xs text-slate-400">
                ثبت‌شده در {formatDateTime(customer.createdAtUtc)}
              </p>
            </div>
            <button
              aria-label="بستن"
              className="grid size-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-600 transition hover:bg-slate-200"
              onClick={onClose}
              type="button"
            >
              <AppIcon name="close" size={19} />
            </button>
          </div>
          <nav className="mt-4 grid grid-cols-2 rounded-xl bg-slate-100 p-1">
            <button
              className={`rounded-lg px-3 py-2.5 text-sm font-black transition ${tab === "profile" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
              onClick={() => setTab("profile")}
              type="button"
            >
              مشخصات مشتری
            </button>
            <button
              className={`rounded-lg px-3 py-2.5 text-sm font-black transition ${tab === "retail" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
              onClick={() => setTab("retail")}
              type="button"
            >
              کالاها، خدمات و تعاملات
            </button>
          </nav>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {tab === "profile" ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-black text-ink-950">
                    شناسه و رضایت ارتباطی
                  </h3>
                  <p className="mt-1 text-xs text-slate-400">
                    اطلاعات مورد استفاده برای فروش، پیگیری و کمپین
                  </p>
                </div>
                {canManageProfile ? (
                  <Button
                    loading={profile.isFetching}
                    onClick={startProfileEdit}
                  >
                    ویرایش پرونده
                  </Button>
                ) : null}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <article className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-xs font-bold text-slate-400">نام مشتری</p>
                  <p className="mt-2 font-black text-slate-800">
                    {contactName(customer)}
                  </p>
                </article>
                <article className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-xs font-bold text-slate-400">وضعیت</p>
                  <p className="mt-2 font-black text-slate-800">
                    {customer.status === "active" ? "فعال" : "غیرفعال"}
                  </p>
                </article>
                <article className="rounded-2xl border border-slate-200 bg-white p-4 sm:col-span-2">
                  <p className="text-xs font-bold text-slate-400">
                    راه‌های ارتباطی
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {customer.contactPoints.length ? (
                      customer.contactPoints.map((point) => (
                        <div
                          className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5"
                          key={point.id}
                        >
                          <span className="text-xs font-bold text-slate-500">
                            {point.type === "mobile" ? "موبایل" : "ایمیل"}
                          </span>
                          <span
                            className="font-sans text-sm font-bold text-slate-800"
                            dir="ltr"
                          >
                            {point.value}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-400">
                        راه ارتباطی ثبت نشده است.
                      </p>
                    )}
                  </div>
                </article>
                <article className="rounded-2xl border border-slate-200 bg-white p-4 sm:col-span-2">
                  <p className="text-xs font-bold text-slate-400">آدرس‌ها</p>
                  <div className="mt-3 grid gap-2">
                    {customer.addresses?.length ? (
                      customer.addresses.map((address) => (
                        <div
                          className="rounded-xl bg-slate-50 px-3 py-2.5"
                          key={address.id}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-bold text-slate-700">
                              {[address.label, address.city, address.province]
                                .filter(Boolean)
                                .join(" · ") || "آدرس ثبت‌شده"}
                            </span>
                            {address.isPrimary ? (
                              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-bold text-brand-700">
                                اصلی
                              </span>
                            ) : null}
                          </div>
                          <p className="mt-1 text-xs text-slate-500">
                            {address.addressLine || "جزئیات آدرس ثبت نشده"}
                            {address.postalCode
                              ? ` · کدپستی: ${address.postalCode}`
                              : ""}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-400">
                        آدرسی ثبت نشده است.
                      </p>
                    )}
                  </div>
                </article>
                <article className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-xs font-bold text-slate-400">
                    برچسب‌های مشتری
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {customer.tags?.length ? (
                      customer.tags.map((tag) => (
                        <span
                          className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-700"
                          key={tag}
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <p className="text-sm text-slate-400">
                        برچسبی ثبت نشده است.
                      </p>
                    )}
                  </div>
                </article>
                <article className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-xs font-bold text-slate-400">
                    رضایت دریافت پیام
                  </p>
                  <div className="mt-3 grid gap-2">
                    {(["sms", "email"] as ContactConsentChannel[]).map(
                      (channel) => {
                        const consent = currentConsent(customer, channel);
                        return (
                          <div
                            className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2"
                            key={channel}
                          >
                            <span className="text-xs font-bold text-slate-600">
                              {channel === "sms" ? "پیامک" : "ایمیل"}
                            </span>
                            <span
                              className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${consentTone[consent.status]}`}
                            >
                              {consentLabel[consent.status]}
                            </span>
                          </div>
                        );
                      },
                    )}
                  </div>
                </article>
              </div>
            </div>
          ) : !canViewRetailHistory ? (
            <div className="grid min-h-64 place-items-center rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center">
              <div>
                <AppIcon
                  className="mx-auto text-slate-300"
                  name="box"
                  size={34}
                />
                <p className="mt-3 font-black text-slate-600">
                  قابلیت علاقه‌مندی کالا و خدمت در پلن فعال نیست.
                </p>
              </div>
            </div>
          ) : history.isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((item) => (
                <div
                  className="h-32 animate-pulse rounded-2xl bg-slate-200"
                  key={item}
                />
              ))}
            </div>
          ) : history.isError ? (
            <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4 text-sm font-bold text-rose-700">
              <p>{getErrorMessage(history.error)}</p>
              <button
                className="mt-3 rounded-xl bg-white px-3 py-2 text-xs"
                onClick={() => void history.refetch()}
                type="button"
              >
                تلاش دوباره
              </button>
            </div>
          ) : (
            <div className="space-y-7">
              <section className="rounded-2xl border border-violet-100 bg-violet-50/50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-black text-violet-950">
                      باشگاه مشتریان
                    </h3>
                    <p className="mt-1 text-xs text-violet-700">
                      امتیاز، سطح وفاداری و گردش‌های قابل استفاده در کمپین
                    </p>
                  </div>
                  {loyalty.data ? (
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-violet-800 shadow-sm">
                      {loyalty.data.vipLevel === "Standard"
                        ? "عادی"
                        : loyalty.data.vipLevel}
                    </span>
                  ) : null}
                </div>
                {loyalty.isLoading ? (
                  <div className="mt-4 h-20 animate-pulse rounded-xl bg-violet-100" />
                ) : loyalty.data ? (
                  <>
                    <div className="mt-4 grid gap-2 sm:grid-cols-3">
                      <div className="rounded-xl bg-white p-3">
                        <p className="text-[11px] font-bold text-slate-400">
                          مانده امتیاز
                        </p>
                        <p className="mt-1 text-lg font-black text-violet-800">
                          {new Intl.NumberFormat("fa-IR").format(
                            loyalty.data.pointsBalance,
                          )}
                        </p>
                      </div>
                      <div className="rounded-xl bg-white p-3">
                        <p className="text-[11px] font-bold text-slate-400">
                          کل امتیاز کسب‌شده
                        </p>
                        <p className="mt-1 text-lg font-black text-violet-800">
                          {new Intl.NumberFormat("fa-IR").format(
                            loyalty.data.lifetimeEarnedPoints,
                          )}
                        </p>
                      </div>
                      <div className="rounded-xl bg-white p-3">
                        <p className="text-[11px] font-bold text-slate-400">
                          ارزش خرید وفاداری
                        </p>
                        <p className="mt-1 text-sm font-black text-violet-800">
                          {money(
                            loyalty.data.lifetimeSpend,
                            loyalty.data.currency,
                          )}
                        </p>
                      </div>
                    </div>
                    {loyalty.data.transactions.length ? (
                      <div className="mt-3 border-t border-violet-100 pt-3">
                        <p className="text-xs font-black text-violet-950">
                          آخرین گردش امتیاز
                        </p>
                        <div className="mt-2 space-y-1.5">
                          {loyalty.data.transactions.slice(0, 5).map((item) => (
                            <div
                              className="flex items-center justify-between gap-3 text-xs"
                              key={item.id}
                            >
                              <span className="min-w-0 truncate text-slate-600">
                                {item.note ||
                                  ({
                                    Earned: "کسب امتیاز",
                                    Redeemed: "مصرف امتیاز",
                                    Returned: "مرجوعی",
                                    Adjustment: "اصلاح دستی",
                                  }[item.transactionType] ??
                                    item.transactionType)}
                              </span>
                              <span
                                className={`shrink-0 font-black ${item.pointsDelta >= 0 ? "text-emerald-700" : "text-rose-700"}`}
                              >
                                {item.pointsDelta > 0 ? "+" : ""}
                                {new Intl.NumberFormat("fa-IR").format(
                                  item.pointsDelta,
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {canManageProfile ? (
                      <div className="mt-4 border-t border-violet-100 pt-3">
                        <p className="text-xs font-black text-violet-950">
                          اصلاح دستی امتیاز
                        </p>
                        <div className="mt-2 grid gap-2 sm:grid-cols-[8rem_1fr_auto]">
                          <input
                            className="input font-sans"
                            dir="ltr"
                            inputMode="numeric"
                            onChange={(event) =>
                              setPointsAdjustment(
                                Number(event.target.value) || 0,
                              )
                            }
                            placeholder="مثلاً ۵۰ یا −۵۰"
                            value={pointsAdjustment || ""}
                          />
                          <input
                            className="input"
                            onChange={(event) =>
                              setPointsAdjustmentNote(event.target.value)
                            }
                            placeholder="علت اصلاح الزامی است"
                            value={pointsAdjustmentNote}
                          />
                          <button
                            className="rounded-xl bg-violet-700 px-4 py-2.5 text-xs font-black text-white disabled:opacity-50"
                            disabled={
                              adjustLoyaltyPoints.isPending ||
                              !pointsAdjustment ||
                              !pointsAdjustmentNote.trim()
                            }
                            onClick={() => adjustLoyaltyPoints.mutate()}
                            type="button"
                          >
                            ثبت
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="mt-4 text-xs text-violet-700">
                    هنوز گردش وفاداری برای این مشتری ثبت نشده است.
                  </p>
                )}
              </section>
              <section>
                <div className="flex items-end justify-between">
                  <div>
                    <h3 className="font-black text-ink-950">
                      خریدها و فاکتورها
                    </h3>
                    <p className="mt-1 text-xs text-slate-400">
                      تمام خریدهای ثبت‌شده، اقلام، مبلغ و وضعیت پیام تشکر
                    </p>
                  </div>
                  <span className="rounded-lg bg-white px-2.5 py-1 text-xs font-bold text-slate-500 shadow-sm">
                    {new Intl.NumberFormat("fa-IR").format(
                      history.data?.purchases?.length ?? 0,
                    )}{" "}
                    فاکتور
                  </span>
                </div>
                <div className="mt-3 space-y-3">
                  {history.data?.purchases?.length ? (
                    history.data.purchases.map((purchase) => (
                      <article
                        className="rounded-2xl border border-emerald-100 bg-white p-4"
                        key={purchase.invoiceId}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="font-black text-slate-800">
                              فاکتور {purchase.invoiceNumber}
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                              {formatDateTime(purchase.createdAtUtc)} ·
                              ثبت‌کننده:{" "}
                              {purchase.sellerMembershipName || "سیستم"}
                            </p>
                          </div>
                          <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
                            {invoiceStatusLabels[purchase.status] ??
                              purchase.status}
                          </span>
                        </div>
                        <PurchaseItems purchase={purchase} />
                        <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500 sm:grid-cols-2">
                          <p>
                            روش پرداخت:{" "}
                            {paymentMethodLabels[purchase.paymentMethod] ??
                              purchase.paymentMethod}
                          </p>
                          <p>
                            وضعیت پرداخت:{" "}
                            {paymentStatusLabels[purchase.paymentStatus] ??
                              purchase.paymentStatus}
                          </p>
                          {purchase.discountAmount > 0 ? (
                            <p>
                              تخفیف:{" "}
                              {money(
                                purchase.discountAmount,
                                purchase.currency,
                              )}
                            </p>
                          ) : null}
                          {purchase.refundedAmount > 0 ? (
                            <p className="text-rose-700">
                              استرداد:{" "}
                              {money(
                                purchase.refundedAmount,
                                purchase.currency,
                              )}
                            </p>
                          ) : null}
                          {purchase.balanceDue > 0 ? (
                            <p className="text-amber-700">
                              مانده:{" "}
                              {money(purchase.balanceDue, purchase.currency)}
                            </p>
                          ) : null}
                          <p className="font-black text-emerald-700">
                            خالص خرید:{" "}
                            {money(purchase.netAmount, purchase.currency)}
                          </p>
                          <p>
                            پیام تشکر:{" "}
                            {deliveryLabels[
                              purchase.thankYouDeliveryStatus || "Skipped"
                            ] ??
                              (purchase.thankYouDeliveryStatus || "ارسال نشده")}
                          </p>
                        </div>
                        {purchase.thankYouDeliveryError ? (
                          <p className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 text-xs text-amber-700">
                            {purchase.thankYouDeliveryError}
                          </p>
                        ) : null}
                        <a
                          className="mt-3 inline-flex rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700"
                          href={purchase.receiptUrl}
                          rel="noreferrer"
                          target="_blank"
                        >
                          مشاهده رسید مشتری
                        </a>
                      </article>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400">
                      هنوز خریدی برای این مشتری ثبت نشده است.
                    </div>
                  )}
                </div>
              </section>

              <section>
                <div className="flex items-end justify-between">
                  <div>
                    <h3 className="font-black text-ink-950">
                      لینک‌ها و صفحات اختصاصی
                    </h3>
                    <p className="mt-1 text-xs text-slate-400">
                      وضعیت ارسال، بازدید، رزرو و اعتبار هر لینک
                    </p>
                  </div>
                  <span className="rounded-lg bg-white px-2.5 py-1 text-xs font-bold text-slate-500 shadow-sm">
                    {new Intl.NumberFormat("fa-IR").format(
                      history.data?.landings?.length ?? 0,
                    )}{" "}
                    مورد
                  </span>
                </div>
                <div className="mt-3 space-y-3">
                  {history.data?.landings.length ? (
                    history.data.landings.map((landing) => {
                      const items = landingItems(landing);
                      return (
                        <article
                          className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                          key={landing.id}
                        >
                          <div className="p-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <p className="font-black text-slate-800">
                                  صفحه معرفی{" "}
                                  {new Intl.NumberFormat("fa-IR").format(
                                    items.length,
                                  )}{" "}
                                  مورد
                                </p>
                                <p className="mt-1 text-xs text-slate-400">
                                  {items
                                    .map((item) => itemTypeLabel(item.itemType))
                                    .join("، ")}
                                </p>
                              </div>
                              <span
                                className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${statusTone[landing.status] ?? statusTone.Created}`}
                              >
                                {statusLabels[landing.status] ?? landing.status}
                              </span>
                            </div>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                              {items.map((item) => (
                                <div
                                  className="flex min-w-0 gap-3 rounded-xl bg-slate-50 p-3"
                                  key={item.catalogItemId || item.productId}
                                >
                                  <div className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-lg bg-white text-brand-600">
                                    {item.imageUrl &&
                                    /\.(png|jpe?g|webp)(\?.*)?$/i.test(
                                      item.imageUrl,
                                    ) ? (
                                      <img
                                        alt=""
                                        className="size-full object-cover"
                                        src={item.imageUrl}
                                      />
                                    ) : (
                                      <AppIcon name="box" size={20} />
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                                      {itemTypeLabel(item.itemType)}
                                    </span>
                                    <p className="mt-1 truncate text-xs font-black text-slate-800">
                                      {item.name || item.productName}
                                    </p>
                                    <p className="mt-1 text-[11px] font-bold text-brand-700">
                                      {money(item.price, item.currency)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="mt-3 grid gap-1 border-t border-slate-100 pt-3 text-xs text-slate-500 sm:grid-cols-2">
                              <p>
                                ساخت: {formatDateTime(landing.createdAtUtc)}
                              </p>
                              <p>
                                انقضا:{" "}
                                {landing.expiresAtUtc
                                  ? formatDateTime(landing.expiresAtUtc)
                                  : "بدون انقضا"}
                              </p>
                              <p>
                                ارسال پیامک:{" "}
                                {landing.deliveryStatus === "Sent"
                                  ? "موفق"
                                  : landing.deliveryStatus || "ثبت نشده"}
                              </p>
                              {landing.revokedAtUtc ? (
                                <p>
                                  غیرفعال‌سازی:{" "}
                                  {formatDateTime(landing.revokedAtUtc)}
                                  {landing.revokedByName
                                    ? ` توسط ${landing.revokedByName}`
                                    : ""}
                                </p>
                              ) : null}
                            </div>
                            {landing.deliveryError ? (
                              <p className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 text-xs text-amber-700">
                                {landing.deliveryError}
                              </p>
                            ) : null}
                          </div>
                          <div className="border-t border-slate-100 px-4 py-3">
                            <EventSummary
                              events={landing.events}
                              items={items}
                            />
                            <div className="mt-3 flex flex-wrap gap-2">
                              {landing.isAvailable ? (
                                <>
                                  <a
                                    className="rounded-lg bg-brand-50 px-3 py-2 text-xs font-bold text-brand-700"
                                    href={landing.landingUrl}
                                    rel="noreferrer"
                                    target="_blank"
                                  >
                                    مشاهده صفحه
                                  </a>
                                  <button
                                    className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600"
                                    onClick={() =>
                                      void copy(landing.landingUrl)
                                    }
                                    type="button"
                                  >
                                    کپی لینک
                                  </button>
                                  {canRevokeLanding ? (
                                    <Popconfirm
                                      cancelText="انصراف"
                                      okButtonProps={{ danger: true }}
                                      okText="غیرفعال شود"
                                      onConfirm={() =>
                                        revoke.mutate(landing.id)
                                      }
                                      title="این لینک دیگر برای مشتری باز نخواهد شد. ادامه می‌دهید؟"
                                    >
                                      <button
                                        className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 disabled:opacity-50"
                                        disabled={revoke.isPending}
                                        type="button"
                                      >
                                        غیرفعال‌کردن لینک
                                      </button>
                                    </Popconfirm>
                                  ) : null}
                                </>
                              ) : (
                                <span className="text-xs font-bold text-slate-400">
                                  این لینک دیگر در دسترس مشتری نیست.
                                </span>
                              )}
                            </div>
                          </div>
                        </article>
                      );
                    })
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400">
                      هنوز صفحه اختصاصی برای این مشتری ساخته نشده است.
                    </div>
                  )}
                </div>
              </section>

              <section>
                <div className="flex items-end justify-between">
                  <div>
                    <h3 className="font-black text-ink-950">
                      تاریخچه علاقه‌مندی کالا و خدمات
                    </h3>
                    <p className="mt-1 text-xs text-slate-400">
                      مورد معرفی‌شده، وضعیت، ثبت‌کننده و زمان تعامل
                    </p>
                  </div>
                  <span className="rounded-lg bg-white px-2.5 py-1 text-xs font-bold text-slate-500 shadow-sm">
                    {new Intl.NumberFormat("fa-IR").format(
                      history.data?.interests.length ?? 0,
                    )}{" "}
                    مورد
                  </span>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {history.data?.interests.length ? (
                    history.data.interests.map((interest) => (
                      <article
                        className="rounded-2xl border border-slate-200 bg-white p-4"
                        key={interest.id}
                      >
                        <div className="flex items-start gap-3">
                          <div className="grid size-11 shrink-0 place-items-center overflow-hidden rounded-xl bg-slate-100 text-brand-600">
                            {interest.productImageUrl &&
                            /\.(png|jpe?g|webp)(\?.*)?$/i.test(
                              interest.productImageUrl,
                            ) ? (
                              <img
                                alt=""
                                className="size-full object-cover"
                                src={interest.productImageUrl}
                              />
                            ) : (
                              <AppIcon
                                name={
                                  interest.itemType === "Service"
                                    ? "sparkles"
                                    : "box"
                                }
                                size={19}
                              />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex min-w-0 items-center gap-2">
                              <p className="truncate font-black text-slate-800">
                                {interest.productName}
                              </p>
                              <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                                {interest.itemType === "Service"
                                  ? "خدمت"
                                  : "کالا"}
                              </span>
                            </div>
                            <span className="mt-1 inline-flex rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-bold text-brand-700">
                              {interestLabels[interest.interestType] ??
                                interest.interestType}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 border-t border-slate-100 pt-3 text-xs leading-6 text-slate-500">
                          <p>
                            ثبت‌کننده: {interest.salesPersonName || "سیستم"}
                          </p>
                          <p>زمان: {formatDateTime(interest.occurredAtUtc)}</p>
                        </div>
                      </article>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400 sm:col-span-2">
                      هنوز علاقه‌مندی برای کالا یا خدمتی ثبت نشده است.
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
        <Modal
          cancelText="انصراف"
          destroyOnHidden
          footer={null}
          onCancel={() => setEditingProfile(false)}
          open={editingProfile}
          title="ویرایش پرونده مشتری"
          width={760}
        >
          <Form<CustomerProfileForm>
            form={profileForm}
            layout="vertical"
            onFinish={submitProfile}
          >
            <div className="rounded-xl bg-slate-50 p-3 text-xs leading-6 text-slate-500">
              این اطلاعات برای معرفی سریع کالا، شخصی‌سازی پیام‌ها و ساخت
              گروه‌های کمپین استفاده می‌شود.
            </div>
            <Form.List name="addresses">
              {(fields, { add, remove }) => (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-slate-700">آدرس‌ها</p>
                    <Button
                      onClick={() => add({ isPrimary: fields.length === 0 })}
                      size="small"
                      type="dashed"
                    >
                      افزودن آدرس
                    </Button>
                  </div>
                  {fields.map((field, index) => (
                    <div
                      className="rounded-xl border border-slate-200 p-3"
                      key={field.key}
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-500">
                          آدرس{" "}
                          {new Intl.NumberFormat("fa-IR").format(index + 1)}
                        </span>
                        <Button
                          danger
                          onClick={() => remove(field.name)}
                          size="small"
                          type="text"
                        >
                          حذف
                        </Button>
                      </div>
                      <div className="grid gap-x-3 sm:grid-cols-2">
                        <Form.Item label="عنوان" name={[field.name, "label"]}>
                          <Input placeholder="خانه، محل کار، شعبه..." />
                        </Form.Item>
                        <Form.Item
                          label="استان"
                          name={[field.name, "province"]}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item label="شهر" name={[field.name, "city"]}>
                          <Input />
                        </Form.Item>
                        <Form.Item
                          label="کدپستی"
                          name={[field.name, "postalCode"]}
                        >
                          <Input dir="ltr" />
                        </Form.Item>
                        <Form.Item
                          className="sm:col-span-2"
                          label="نشانی"
                          name={[field.name, "addressLine"]}
                        >
                          <Input.TextArea
                            autoSize={{ minRows: 2, maxRows: 3 }}
                          />
                        </Form.Item>
                        <Form.Item
                          className="mb-0"
                          name={[field.name, "isPrimary"]}
                          valuePropName="checked"
                        >
                          <label className="flex cursor-pointer items-center gap-2 text-xs font-bold text-slate-600">
                            <input type="checkbox" /> آدرس اصلی
                          </label>
                        </Form.Item>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Form.List>
            <Form.Item
              className="mt-5"
              label="برچسب‌ها"
              name="tags"
              extra="برچسب‌ها را با ویرگول فارسی یا انگلیسی جدا کنید؛ مانند VIP، مشتری وفادار"
            >
              <Input placeholder="VIP، مشتری وفادار" />
            </Form.Item>
            <div className="grid gap-x-3 sm:grid-cols-2">
              <Form.Item label="رضایت دریافت پیامک" name="smsConsent">
                <Select
                  options={[
                    { value: "unknown", label: "نامشخص" },
                    { value: "granted", label: "مجاز" },
                    { value: "withdrawn", label: "لغو شده" },
                  ]}
                />
              </Form.Item>
              <Form.Item label="منبع رضایت پیامک" name="smsConsentSource">
                <Input placeholder="مثلاً فرم ثبت‌نام" />
              </Form.Item>
              <Form.Item label="رضایت دریافت ایمیل" name="emailConsent">
                <Select
                  options={[
                    { value: "unknown", label: "نامشخص" },
                    { value: "granted", label: "مجاز" },
                    { value: "withdrawn", label: "لغو شده" },
                  ]}
                />
              </Form.Item>
              <Form.Item label="منبع رضایت ایمیل" name="emailConsentSource">
                <Input placeholder="مثلاً ثبت‌نام سایت" />
              </Form.Item>
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <Button onClick={() => setEditingProfile(false)}>انصراف</Button>
              <Button
                htmlType="submit"
                loading={saveProfile.isPending}
                type="primary"
              >
                ذخیره پرونده
              </Button>
            </div>
          </Form>
        </Modal>
      </section>
    </div>
  );
};
